import React from "react";

import { motion } from "framer-motion";

import {
  Clock3,
  AlertCircle,
  User,
} from "lucide-react";

const statusColors = {
  Open:
    "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",

  "In Progress":
    "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",

  Resolved:
    "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300",

  Closed:
    "bg-gray-200 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300",
};

const priorityColors = {
  Low:
    "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",

  Medium:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300",

  High:
    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
};

const categoryColors = {
  Bug: "bg-red-500/10 text-red-400",
  Suggestion:
    "bg-purple-500/10 text-purple-400",
  Payment:
    "bg-green-500/10 text-green-400",
  Content:
    "bg-blue-500/10 text-blue-400",
  MCQ:
    "bg-orange-500/10 text-orange-400",
  Mains:
    "bg-pink-500/10 text-pink-400",
  UI:
    "bg-cyan-500/10 text-cyan-400",
  Other:
    "bg-gray-500/10 text-gray-400",
};

const AdminTicketList = ({
  tickets,
  loading,
  selectedTicket,
  setSelectedTicket,
}) => {

  // ====================================
  // LOADING
  // ====================================
  if (loading) {
    return (
      <div className="p-4 space-y-4">

        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            className="
              h-32
              rounded-2xl
              bg-gray-200
              dark:bg-gray-800
              animate-pulse
            "
          />
        ))}
      </div>
    );
  }

  // ====================================
  // EMPTY
  // ====================================
  if (

!Array.isArray(
tickets
)

||

tickets.length===0

) {
    return (
      <div
        className="
          h-full
          flex
          flex-col
          items-center
          justify-center
          text-center
          px-6
        "
      >

        <div
          className="
            w-20
            h-20
            rounded-full
            bg-gray-200
            dark:bg-gray-800
            flex
            items-center
            justify-center
            mb-4
          "
        >
          <AlertCircle
            size={36}
            className="
              text-gray-500
            "
          />
        </div>

        <h3
          className="
            text-lg
            font-semibold
            text-gray-700
            dark:text-white
          "
        >
          No Support Tickets
        </h3>

        <p
          className="
            text-sm
            text-gray-500
            mt-2
          "
        >
          User conversations will
          appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3">

      {(

Array.isArray(
tickets
)

?

tickets

:

[]

).map((ticket, index) => {

        const isSelected =
          selectedTicket?._id ===
          ticket._id;

        return (
          <motion.div
            key={ticket._id}

            initial={{
              opacity: 0,
              x: -10,
            }}

            animate={{
              opacity: 1,
              x: 0,
            }}

            transition={{
              delay: index * 0.03,
            }}

            onClick={() =>
              setSelectedTicket(ticket)
            }

            className={`
              relative
              p-4
              rounded-2xl
              cursor-pointer
              border
              transition-all
              duration-300
              group

              ${
                isSelected
                  ? `
                    bg-gradient-to-r
                    from-blue-500/10
                    to-purple-500/10
                    border-blue-500/30
                    shadow-lg
                  `
                  : `
                    bg-white
                    dark:bg-gray-900
                    border-gray-200
                    dark:border-gray-800
                    hover:border-blue-400/40
                    hover:shadow-md
                  `
              }
            `}
          >

            {/* UNREAD INDICATOR */}
            {ticket.adminUnreadCount >
              0 && (
              <div
                className="
                  absolute
                  top-3
                  right-3
                  w-3
                  h-3
                  rounded-full
                  bg-blue-500
                  animate-pulse
                  shadow-lg
                "
              />
            )}

            {/* USER */}
            <div
              className="
                flex
                items-center
                gap-2
                mb-3
              "
            >
              <div
                className="
                  w-10
                  h-10
                  rounded-full
                  bg-gradient-to-r
                  from-blue-500
                  to-purple-500
                  flex
                  items-center
                  justify-center
                  text-white
                "
              >
                <User size={18} />
              </div>

              <div className="flex-1 min-w-0">

                <h3
                  className="
                    font-semibold
                    text-gray-800
                    dark:text-white
                    truncate
                  "
                >
                  {ticket?.user?.name ||
                    "User"}
                </h3>

                <p
                  className="
                    text-xs
                    text-gray-500
                    truncate
                  "
                >
                  {ticket?.user?.email}
                </p>
              </div>

              {/* UNREAD COUNT */}
              {ticket.adminUnreadCount >
                0 && (
                <div
                  className="
                    min-w-[22px]
                    h-6
                    px-2
                    rounded-full
                    bg-blue-500
                    text-white
                    text-xs
                    font-bold
                    flex
                    items-center
                    justify-center
                  "
                >
                  {ticket.adminUnreadCount}
                </div>
              )}
            </div>

            {/* SUBJECT */}
            <h2
              className="
                text-sm
                font-semibold
                text-gray-800
                dark:text-white
                line-clamp-1
              "
            >
              {ticket.subject}
            </h2>

            {/* LATEST MESSAGE */}
            <p
              className="
                mt-1
                text-sm
                text-gray-500
                dark:text-gray-400
                line-clamp-2
              "
            >
              {ticket.latestMessage}
            </p>

            {/* BADGES */}
            <div
              className="
                mt-4
                flex
                flex-wrap
                items-center
                gap-2
              "
            >

              {/* STATUS */}
              <span
                className={`
                  px-3
                  py-1
                  rounded-full
                  text-xs
                  font-medium
                  ${statusColors[
                    ticket.status
                  ]}
                `}
              >
                {ticket.status}
              </span>

              {/* CATEGORY */}
              <span
                className={`
                  px-3
                  py-1
                  rounded-full
                  text-xs
                  font-medium
                  ${categoryColors[
                    ticket.category
                  ]}
                `}
              >
                {ticket.category}
              </span>

              {/* PRIORITY */}
              <span
                className={`
                  px-3
                  py-1
                  rounded-full
                  text-xs
                  font-medium
                  ${priorityColors[
                    ticket.priority
                  ]}
                `}
              >
                {ticket.priority}
              </span>
            </div>

            {/* FOOTER */}
            <div
              className="
                mt-4
                flex
                items-center
                justify-end
                text-xs
                text-gray-500
                gap-1
              "
            >
              <Clock3 size={12} />

              {new Date(
                ticket.lastMessageAt
              ).toLocaleString()}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default AdminTicketList;